import React, { useState, useEffect, useRef } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

const API_BASE = BASE_URL;

// Faz chamada multipart (FormData) com autenticação
async function apiCallFormData(endpoint, method, formData) {
  const token = getAuthToken();
  const url = `${API_BASE}/${endpoint}`;
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { method, headers, body: formData });
  const text = await response.text();
  if (!response.ok) throw new Error(text || `Erro ${response.status}`);
  try { return JSON.parse(text); } catch { return text; }
}

// Faz chamada JSON (GET/DELETE) com autenticação
async function apiCallJson(endpoint, method = 'GET') {
  const token = getAuthToken();
  const url = `${API_BASE}/${endpoint}`;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { method, headers });
  const text = await response.text();
  if (!response.ok) throw new Error(text || `Erro ${response.status}`);
  try { return JSON.parse(text); } catch { return text; }
}

function formatarData(dataStr) {
  if (!dataStr) return '—';
  try {
    return new Date(dataStr).toLocaleDateString('pt-BR');
  } catch { return dataStr; }
}

function StatusBadge({ status }) {
  const cor = status === 'Ativo' ? '#16a34a' : '#dc2626';
  const bg  = status === 'Ativo' ? '#dcfce7' : '#fee2e2';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, color: cor, background: bg, border: `1px solid ${cor}22`
    }}>
      {status || 'Desconhecido'}
    </span>
  );
}

export default function CertificadoPlugNotasDropdown() {
  // ── Listagem ──────────────────────────────────────────────────────────────
  const [certificados, setCertificados]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [erro, setErro]                   = useState('');

  // ── Formulário de upload ──────────────────────────────────────────────────
  const [mostrarForm, setMostrarForm]     = useState(false);
  const [arquivo, setArquivo]             = useState(null);
  const [senha, setSenha]                 = useState('');
  const [email, setEmail]                 = useState('');
  const [mostrarSenha, setMostrarSenha]   = useState(false);
  const [enviando, setEnviando]           = useState(false);
  const [msg, setMsg]                     = useState('');
  const [msgTipo, setMsgTipo]             = useState(''); // 'sucesso' | 'erro'
  const fileInputRef                       = useRef(null);

  // ── Atualização de certificado ─────────────────────────────────────────────
  const [editandoId, setEditandoId]       = useState(null);
  const [arquivoUpd, setArquivoUpd]       = useState(null);
  const [senhaUpd, setSenhaUpd]           = useState('');
  const [emailUpd, setEmailUpd]           = useState('');
  const [mostrarSenhaUpd, setMostrarSenhaUpd] = useState(false);
  const [enviandoUpd, setEnviandoUpd]     = useState(false);
  const fileInputUpdRef                    = useRef(null);

  // ── Exclusão ───────────────────────────────────────────────────────────────
  const [excluindoId, setExcluindoId]     = useState(null);
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState(null); // id

  // ── Drag & Drop ───────────────────────────────────────────────────────────
  const [dragging, setDragging]           = useState(false);
  const [draggingUpd, setDraggingUpd]     = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Carregar certificados
  // ─────────────────────────────────────────────────────────────────────────
  const carregarCertificados = async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiCallJson('plugnotas/certificado/locais');
      setCertificados(Array.isArray(data) ? data : []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregarCertificados(); }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Cadastrar certificado
  // ─────────────────────────────────────────────────────────────────────────
  const handleCadastrar = async (e) => {
    e.preventDefault();
    if (!arquivo) { setMsg('Selecione o arquivo do certificado (.pfx ou .p12).'); setMsgTipo('erro'); return; }
    if (!senha)   { setMsg('Informe a senha do certificado.');                     setMsgTipo('erro'); return; }

    const fd = new FormData();
    fd.append('Arquivo', arquivo);
    fd.append('Senha',   senha);
    if (email) fd.append('Email', email);

    setEnviando(true);
    setMsg(''); setMsgTipo('');
    try {
      await apiCallFormData('plugnotas/certificado', 'POST', fd);
      setMsg('Certificado cadastrado com sucesso!');
      setMsgTipo('sucesso');
      setArquivo(null); setSenha(''); setEmail('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setMostrarForm(false);
      carregarCertificados();
    } catch (e) {
      setMsg(`Erro ao cadastrar: ${e.message}`);
      setMsgTipo('erro');
    } finally {
      setEnviando(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Atualizar certificado
  // ─────────────────────────────────────────────────────────────────────────
  const handleAtualizar = async (e, id) => {
    e.preventDefault();
    if (!arquivoUpd) { alert('Selecione o novo arquivo do certificado (.pfx ou .p12).'); return; }
    if (!senhaUpd)   { alert('Informe a senha do novo certificado.'); return; }

    const fd = new FormData();
    fd.append('Arquivo', arquivoUpd);
    fd.append('Senha',   senhaUpd);
    if (emailUpd) fd.append('Email', emailUpd);

    setEnviandoUpd(true);
    try {
      await apiCallFormData(`plugnotas/certificado/${id}`, 'PUT', fd);
      setMsg('Certificado atualizado com sucesso!');
      setMsgTipo('sucesso');
      setEditandoId(null);
      setArquivoUpd(null); setSenhaUpd(''); setEmailUpd('');
      if (fileInputUpdRef.current) fileInputUpdRef.current.value = '';
      carregarCertificados();
    } catch (e) {
      setMsg(`Erro ao atualizar: ${e.message}`);
      setMsgTipo('erro');
    } finally {
      setEnviandoUpd(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Excluir certificado
  // ─────────────────────────────────────────────────────────────────────────
  const handleExcluir = async (id) => {
    setExcluindoId(id);
    try {
      await apiCallJson(`plugnotas/certificado/${id}`, 'DELETE');
      setMsg('Certificado excluído com sucesso!');
      setMsgTipo('sucesso');
      setConfirmacaoExclusao(null);
      carregarCertificados();
    } catch (e) {
      setMsg(`Erro ao excluir: ${e.message}`);
      setMsgTipo('erro');
    } finally {
      setExcluindoId(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Drag & Drop helpers
  // ─────────────────────────────────────────────────────────────────────────
  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setArquivo(file);
  };
  const handleDropUpd = (e) => {
    e.preventDefault(); setDraggingUpd(false);
    const file = e.dataTransfer.files[0];
    if (file) setArquivoUpd(file);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="container-geral fundo-branco" style={{ background: '#f5f5f5', padding: '24px 16px' }}>
      {loading && <LoadingOverlay />}
      <MobileAddListener onOpen={() => { setMostrarForm(f => !f); setMsg(''); }} />

      {/* ── Cabeçalho ── */}
      <div style={{ maxWidth: 900, margin: '0 auto 24px auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#1e293b', fontWeight: 700 }}>
            🔐 Certificado Digital — PlugNotas
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Gerencie o certificado A1 (.pfx / .p12) utilizado para emissão de notas fiscais via PlugNotas.
          </p>
        </div>
        <button
          onClick={() => { setMostrarForm(f => !f); setMsg(''); }}
          style={{
            background: mostrarForm ? '#64748b' : '#2563eb',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontWeight: 600, fontSize: 14,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          {mostrarForm ? '✕ Fechar' : '+ Novo Certificado'}
        </button>
      </div>

      {/* ── Mensagem global ── */}
      {msg && (
        <div style={{
          maxWidth: 900, margin: '0 auto 16px auto',
          padding: '12px 18px', borderRadius: 8,
          background: msgTipo === 'sucesso' ? '#dcfce7' : '#fee2e2',
          color:      msgTipo === 'sucesso' ? '#15803d' : '#b91c1c',
          border: `1px solid ${msgTipo === 'sucesso' ? '#86efac' : '#fca5a5'}`,
          fontSize: 14, fontWeight: 500
        }}>
          {msgTipo === 'sucesso' ? '✅ ' : '❌ '}{msg}
          <button onClick={() => setMsg('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 16, color: 'inherit' }}>×</button>
        </div>
      )}

      {/* ── Formulário de novo certificado ── */}
      {mostrarForm && (
        <div style={{
          maxWidth: 900, margin: '0 auto 24px auto',
          background: '#fff', borderRadius: 12, padding: '28px 28px 20px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#1e293b', fontWeight: 700 }}>
            📤 Cadastrar Novo Certificado
          </h3>
          <form onSubmit={handleCadastrar}>
            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? '#2563eb' : arquivo ? '#16a34a' : '#cbd5e1'}`,
                borderRadius: 10, padding: '32px 20px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 20,
                background: dragging ? '#eff6ff' : arquivo ? '#f0fdf4' : '#f8fafc',
                transition: 'all .2s'
              }}
            >
              <div style={{ fontSize: 36, marginBottom: 8 }}>{arquivo ? '✅' : '📁'}</div>
              {arquivo
                ? <p style={{ margin: 0, color: '#16a34a', fontWeight: 600, fontSize: 14 }}>{arquivo.name}</p>
                : <>
                    <p style={{ margin: 0, color: '#475569', fontWeight: 600, fontSize: 14 }}>Arraste o certificado aqui ou clique para selecionar</p>
                    <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: 12 }}>Aceita: .pfx, .p12</p>
                  </>
              }
              <input
                ref={fileInputRef}
                type="file"
                accept=".pfx,.p12"
                style={{ display: 'none' }}
                onChange={e => setArquivo(e.target.files[0] || null)}
              />
            </div>

            {/* Grid de campos */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px', marginBottom: 20 }}>
              {/* Senha */}
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                  Senha do Certificado <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={e => setSenha(e.target.value)}
                    placeholder="Senha do arquivo .pfx / .p12"
                    style={{ width: '100%', paddingRight: 40, boxSizing: 'border-box' }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(v => !v)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#64748b' }}
                    title={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {mostrarSenha ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* E-mail */}
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                  E-mail para Notificações
                  <span style={{ marginLeft: 6, color: '#94a3b8', fontWeight: 400 }}>(opcional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="email@empresa.com.br"
                />
              </div>
            </div>

            {/* Aviso */}
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
              padding: '10px 14px', fontSize: 12, color: '#92400e', marginBottom: 20
            }}>
              ⚠️ O CNPJ e dados da empresa serão obtidos automaticamente do cadastro da empresa logada.
            </div>

            {/* Botões */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { setMostrarForm(false); setArquivo(null); setSenha(''); setEmail(''); setMsg(''); }}
                style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={enviando}
                style={{
                  background: enviando ? '#93c5fd' : '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '10px 28px',
                  fontWeight: 600, fontSize: 14, cursor: enviando ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8
                }}
              >
                {enviando ? '⏳ Enviando...' : '🚀 Cadastrar Certificado'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Erro de carregamento ── */}
      {erro && !loading && (
        <div style={{ maxWidth: 900, margin: '0 auto 16px auto', padding: '14px 18px', borderRadius: 8, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', fontSize: 14 }}>
          ❌ {erro}
          <button onClick={carregarCertificados} style={{ marginLeft: 12, background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>Tentar novamente</button>
        </div>
      )}

      {/* ── Lista de certificados ── */}
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {!loading && certificados.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
            <p style={{ margin: 0, color: '#64748b', fontSize: 15, fontWeight: 500 }}>
              Nenhum certificado cadastrado
            </p>
            <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 13 }}>
              Clique em <strong>+ Novo Certificado</strong> para cadastrar o certificado A1 da sua empresa.
            </p>
          </div>
        )}

        {certificados.map(cert => (
          <div key={cert.id} style={{
            background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
            boxShadow: '0 1px 6px rgba(0,0,0,0.05)', marginBottom: 16,
            overflow: 'hidden'
          }}>
            {/* Cabeçalho do card */}
            <div style={{
              padding: '18px 22px', display: 'flex', alignItems: 'flex-start',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
              borderBottom: editandoId === cert.id ? '1px solid #e2e8f0' : 'none'
            }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 22 }}>🏢</span>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    {cert.empresaNome || cert.nome || 'Certificado'}
                  </span>
                  <StatusBadge status={cert.status} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 20px' }}>
                  {cert.cnpj && (
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      <strong>CNPJ:</strong> {cert.cnpj}
                    </span>
                  )}
                  {cert.email && (
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      <strong>E-mail:</strong> {cert.email}
                    </span>
                  )}
                  {cert.dataVencimento && (
                    <span style={{
                      fontSize: 12,
                      color: new Date(cert.dataVencimento) < new Date() ? '#dc2626' : '#64748b',
                      fontWeight: new Date(cert.dataVencimento) < new Date() ? 700 : 400
                    }}>
                      <strong>Vencimento:</strong> {formatarData(cert.dataVencimento)}
                      {new Date(cert.dataVencimento) < new Date() && ' ⚠️ VENCIDO'}
                    </span>
                  )}
                  {cert.createdAt && (
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      <strong>Cadastrado em:</strong> {formatarData(cert.createdAt)}
                    </span>
                  )}
                  {cert.plugNotasId && (
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                      PlugNotas ID: {cert.plugNotasId}
                    </span>
                  )}
                </div>
              </div>

              {/* Botões de ação */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => {
                    setEditandoId(editandoId === cert.id ? null : cert.id);
                    setArquivoUpd(null); setSenhaUpd(''); setEmailUpd(cert.email || '');
                    setMsg('');
                  }}
                  style={{
                    background: editandoId === cert.id ? '#64748b' : '#f0f9ff',
                    color:      editandoId === cert.id ? '#fff'    : '#0369a1',
                    border:     `1px solid ${editandoId === cert.id ? '#64748b' : '#bae6fd'}`,
                    borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                  }}
                >
                  {editandoId === cert.id ? '✕ Fechar' : '🔄 Renovar'}
                </button>
                <button
                  onClick={() => setConfirmacaoExclusao(cert.id)}
                  disabled={excluindoId === cert.id}
                  style={{
                    background: '#fff0f0', color: '#dc2626', border: '1px solid #fca5a5',
                    borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13,
                    cursor: excluindoId === cert.id ? 'not-allowed' : 'pointer'
                  }}
                >
                  {excluindoId === cert.id ? '⏳' : '🗑️ Excluir'}
                </button>
              </div>
            </div>

            {/* Formulário de atualização (inline) */}
            {editandoId === cert.id && (
              <div style={{ padding: '20px 22px', background: '#f8fafc' }}>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569', fontWeight: 600 }}>
                  🔄 Substituir certificado — envie o novo arquivo .pfx / .p12
                </p>
                <form onSubmit={e => handleAtualizar(e, cert.id)}>
                  {/* Drop zone atualização */}
                  <div
                    onDragOver={e => { e.preventDefault(); setDraggingUpd(true); }}
                    onDragLeave={() => setDraggingUpd(false)}
                    onDrop={handleDropUpd}
                    onClick={() => fileInputUpdRef.current?.click()}
                    style={{
                      border: `2px dashed ${draggingUpd ? '#2563eb' : arquivoUpd ? '#16a34a' : '#cbd5e1'}`,
                      borderRadius: 8, padding: '20px', textAlign: 'center',
                      cursor: 'pointer', marginBottom: 16,
                      background: draggingUpd ? '#eff6ff' : arquivoUpd ? '#f0fdf4' : '#fff',
                      transition: 'all .2s'
                    }}
                  >
                    {arquivoUpd
                      ? <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 13 }}>✅ {arquivoUpd.name}</span>
                      : <span style={{ color: '#64748b', fontSize: 13 }}>📁 Arraste ou clique para selecionar o novo certificado</span>
                    }
                    <input
                      ref={fileInputUpdRef}
                      type="file"
                      accept=".pfx,.p12"
                      style={{ display: 'none' }}
                      onChange={e => setArquivoUpd(e.target.files[0] || null)}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 16 }}>
                    <div className="form-group">
                      <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                        Nova Senha <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={mostrarSenhaUpd ? 'text' : 'password'}
                          value={senhaUpd}
                          onChange={e => setSenhaUpd(e.target.value)}
                          placeholder="Senha do novo certificado"
                          style={{ width: '100%', paddingRight: 40, boxSizing: 'border-box' }}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarSenhaUpd(v => !v)}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#64748b' }}
                        >
                          {mostrarSenhaUpd ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>
                        E-mail para Notificações
                        <span style={{ marginLeft: 6, color: '#94a3b8', fontWeight: 400 }}>(opcional)</span>
                      </label>
                      <input
                        type="email"
                        value={emailUpd}
                        onChange={e => setEmailUpd(e.target.value)}
                        placeholder="email@empresa.com.br"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => { setEditandoId(null); setArquivoUpd(null); setSenhaUpd(''); setEmailUpd(''); }}
                      style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 7, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={enviandoUpd}
                      style={{
                        background: enviandoUpd ? '#93c5fd' : '#2563eb', color: '#fff',
                        border: 'none', borderRadius: 7, padding: '9px 22px',
                        fontWeight: 600, fontSize: 13, cursor: enviandoUpd ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {enviandoUpd ? '⏳ Atualizando...' : '✅ Salvar novo certificado'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Modal de confirmação de exclusão ── */}
      {confirmacaoExclusao !== null && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '32px 28px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)', maxWidth: 420, width: '90%', textAlign: 'center'
          }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: '0 0 10px', color: '#1e293b', fontSize: 18 }}>Excluir Certificado</h3>
            <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14 }}>
              Tem certeza que deseja excluir este certificado? Esta ação também o remove do PlugNotas e <strong>não pode ser desfeita</strong>.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmacaoExclusao(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleExcluir(confirmacaoExclusao)}
                disabled={excluindoId !== null}
                style={{
                  background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: 14,
                  cursor: excluindoId !== null ? 'not-allowed' : 'pointer'
                }}
              >
                {excluindoId !== null ? '⏳ Excluindo...' : '🗑️ Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
